import { useState } from 'react'

import { Alert, Button, PinInput, Stack, Text, TextInput } from '@mantine/core'

import { useMutation } from '@redwoodjs/web'

import {
  REQUEST_VOE_OTP,
  VERIFY_VOE_OTP,
} from 'src/graphql/voeResponseAuth.graphql'

interface VoEOtpGateProps {
  voeId: string
  token: string
  allowedDomain: string | null
  onAuthorized: (sessionToken: string) => void
}

const VoEOtpGate = ({
  voeId,
  token,
  allowedDomain,
  onAuthorized,
}: VoEOtpGateProps) => {
  const [step, setStep] = useState<'identity' | 'otp'>('identity')
  const [firstName, setFirstName] = useState('')
  const [lastName, setLastName] = useState('')
  const [email, setEmail] = useState('')
  const [code, setCode] = useState('')
  const [error, setError] = useState<string | null>(null)

  const [requestOtp, { loading: requesting }] = useMutation(REQUEST_VOE_OTP, {
    onCompleted: (data) => {
      if (data.requestVoEOtp.success) {
        setError(null)
        setStep('otp')
      } else {
        setError(data.requestVoEOtp.error)
      }
    },
    onError: (err) => setError(err.message),
  })

  const [verifyOtp, { loading: verifying }] = useMutation(VERIFY_VOE_OTP, {
    onCompleted: (data) => {
      if (data.verifyVoEOtp.sessionToken) {
        sessionStorage.setItem(`voe_session_${voeId}`, data.verifyVoEOtp.sessionToken)
        onAuthorized(data.verifyVoEOtp.sessionToken)
      } else {
        setError(data.verifyVoEOtp.error)
      }
    },
    onError: (err) => setError(err.message),
  })

  const handleRequestOtp = () => {
    if (!firstName.trim() || !lastName.trim() || !email.trim()) {
      setError('All fields are required')
      return
    }
    setError(null)
    requestOtp({
      variables: {
        voeId,
        token,
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        email: email.trim(),
      },
    })
  }

  const handleVerifyOtp = (value: string) => {
    if (value.length === 6) {
      setError(null)
      verifyOtp({ variables: { voeId, token, email: email.trim(), code: value } })
    }
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-doubleNickel-gray-50">
      <div className="w-full max-w-md rounded-lg border border-doubleNickel-gray-200 bg-white p-8 shadow-sm">
        {step === 'identity' ? (
          <Stack gap="md">
            <Text fw={600} size="lg">
              Verify your identity
            </Text>
            <Text size="sm" c="dimmed">
              Enter your name and work email to receive a verification code.
              {allowedDomain && (
                <span className="ml-1 text-doubleNickel-gray-500">
                  Email must be from <strong>@{allowedDomain}</strong>.
                </span>
              )}
            </Text>
            <TextInput
              label="First name"
              value={firstName}
              onChange={(e) => setFirstName(e.currentTarget.value)}
              required
            />
            <TextInput
              label="Last name"
              value={lastName}
              onChange={(e) => setLastName(e.currentTarget.value)}
              required
            />
            <TextInput
              label="Work email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.currentTarget.value)}
              required
            />
            {error && <Alert color="red">{error}</Alert>}
            <Button loading={requesting} onClick={handleRequestOtp} fullWidth>
              Send verification code
            </Button>
          </Stack>
        ) : (
          <Stack gap="md">
            <Text fw={600} size="lg">
              Enter verification code
            </Text>
            <Text size="sm" c="dimmed">
              We sent a 6-digit code to <strong>{email}</strong>. It expires in
              10 minutes.
            </Text>
            <PinInput
              length={6}
              type="number"
              value={code}
              onChange={setCode}
              onComplete={handleVerifyOtp}
              disabled={verifying}
            />
            {error && <Alert color="red">{error}</Alert>}
            <Button
              variant="subtle"
              size="sm"
              onClick={() => {
                setStep('identity')
                setCode('')
                setError(null)
              }}
            >
              Use a different email
            </Button>
          </Stack>
        )}
      </div>
    </div>
  )
}

export default VoEOtpGate
